import React from 'react';
import { Code2, Cpu, Rocket, Heart } from 'lucide-react';
import { profileData } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';

const About = () => {
  const { t } = useLanguage();

  const highlights = [
    {
      icon: Code2,
      title: t('about.highlights.cleanCode.title'),
      description: t('about.highlights.cleanCode.description')
    },
    {
      icon: Cpu,
      title: t('about.highlights.modernTech.title'),
      description: t('about.highlights.modernTech.description')
    },
    {
      icon: Rocket,
      title: t('about.highlights.performance.title'),
      description: t('about.highlights.performance.description')
    },
    {
      icon: Heart,
      title: t('about.highlights.userFocus.title'),
      description: t('about.highlights.userFocus.description')
    }
  ];

  return (
    <section id="about" className="py-32 px-6 relative">
      {/* Background accent */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-1/2 left-1/4 w-[500px] h-[500px] bg-purple-600/10 rounded-full blur-[120px]" />
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Section header */}
        <div className="text-center mb-20">
          <span className="text-purple-400 font-mono text-sm tracking-wider uppercase">{t('about.label')}</span>
          <h2 className="text-4xl md:text-5xl font-bold mt-4 mb-6 text-white">
            {t('about.title')} <span className="gradient-text">{t('about.titleHighlight')}</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-purple-500 to-fuchsia-500 mx-auto rounded-full" />
        </div>

        {/* Bio section */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          {/* Text content */}
          <div className="space-y-6">
            <p className="text-lg text-gray-300 leading-relaxed">
              {t('about.bio')}
            </p>
            <p className="text-lg text-gray-400 leading-relaxed">
              {t('about.bio2')}
            </p>
            
            {/* Quick info */}
            <div className="flex flex-wrap gap-4 pt-4">
              <div className="px-4 py-2 rounded-lg bg-purple-500/10 border border-purple-500/30">
                <span className="text-purple-300 font-mono text-sm">📍 {t('about.location')}</span>
              </div>
              <div className="px-4 py-2 rounded-lg bg-green-500/10 border border-green-500/30">
                <span className="text-green-300 font-mono text-sm">✓ {t('about.freelance')}</span>
              </div>
            </div>
          </div>

          {/* Code block decoration */}
          <div className="relative">
            <div className="bg-[hsl(270,40%,8%)] rounded-2xl border border-purple-500/20 p-6 font-mono text-sm overflow-hidden">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span className="ml-4 text-gray-500 text-xs">developer.js</span>
              </div>
              <pre className="text-gray-300">
                <code>
{`const developer = {
  name: "Ömer Karani Denizoglu",
  role: "Founder & CEO",
  companies: [
    "Karany Holding",
    "Karany Web Solutions B.V."
  ],
  skills: ["JS", "React", "Node"],
  
  buildAmazing: () => true
};`}
                </code>
              </pre>
            </div>
            {/* Glow effect */}
            <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-fuchsia-600 rounded-2xl blur-xl opacity-20 -z-10" />
          </div>
        </div>

        {/* Highlights grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {highlights.map((item, index) => (
            <div 
              key={index}
              className="group p-6 rounded-2xl bg-[hsl(270,40%,8%)] border border-purple-500/10 hover:border-purple-500/40 transition-all duration-300 card-hover"
            >
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center mb-4 group-hover:bg-purple-500/20 transition-colors duration-300">
                <item.icon className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
              <p className="text-gray-400 text-sm">{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default About;
